import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import type { DownloadState, Quality, DownloadResult, SSEPhase } from '../types'
import { downloadVideoSSE, fetchCachedVideo } from '../services/api'

interface UseVideoDownloadReturn {
  state: DownloadState
  phase: SSEPhase | null
  progress: number
  result: DownloadResult | null
  errorMessage: string | null
  sourceUrl: string | null
  download: (url: string, quality: Quality) => void
  reset: () => void
  shareToPhotos: () => Promise<boolean>
  canShare: boolean
}

export function useVideoDownload(): UseVideoDownloadReturn {
  const [state, setState] = useState<DownloadState>('idle')
  const [phase, setPhase] = useState<SSEPhase | null>(null)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<DownloadResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Retained after the download completes so the result actions (e.g. publishing a share link)
  // can refer back to what was downloaded.
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)

  const objectUrlRef = useRef<string | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  const canShare = useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.share) return false
    const isSecure = location.protocol === 'https:' || location.hostname === 'localhost'
    return isSecure
  }, [])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
      }
      cleanupRef.current?.()
    }
  }, [])

  const reset = useCallback(() => {
    cleanupRef.current?.()
    cleanupRef.current = null

    setState('idle')
    setPhase(null)
    setProgress(0)
    setErrorMessage(null)
    setSourceUrl(null)

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    setResult(null)
  }, [])

  const download = useCallback((url: string, quality: Quality) => {
    reset()
    setState('fetching')
    setPhase('fetching')
    setSourceUrl(url)

    const cleanup = downloadVideoSSE(
      url,
      quality,
      (sseProgress) => {
        setPhase(sseProgress.phase)
        if (sseProgress.phase === 'fetching') {
          setState('fetching')
        } else if (sseProgress.phase === 'downloading') {
          setState('downloading')
          if (sseProgress.progress !== undefined) {
            setProgress(sseProgress.progress)
          }
        }
      },
      async (downloadId, filename) => {
        try {
          const downloadResult = await fetchCachedVideo(downloadId, filename)
          objectUrlRef.current = downloadResult.objectUrl
          setResult(downloadResult)
          setState('complete')
          setPhase('complete')

          if (canShare && downloadResult.file && navigator.userActivation?.isActive) {
            try {
              await navigator.share({ files: [downloadResult.file] })
            } catch {
              // Non-fatal: share may fail due to expired user activation or
              // unsupported file type. The "Save to Photos" button is the fallback.
            }
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to retrieve video'
          setErrorMessage(message)
          setState('error')
          setPhase('error')
        }
      },
      (error) => {
        setErrorMessage(error)
        setProgress(0)
        setState('error')
        setPhase('error')
      }
    )

    cleanupRef.current = cleanup
  }, [reset, canShare])

  const shareToPhotos = useCallback(async (): Promise<boolean> => {
    if (!result?.file) return false

    try {
      await navigator.share({ files: [result.file] })
      return true
    } catch {
      return false
    }
  }, [result])

  return {
    state,
    phase,
    progress,
    result,
    errorMessage,
    sourceUrl,
    download,
    reset,
    shareToPhotos,
    canShare,
  }
}
